/**
 * AccountCreationModal.js - Phase E3
 * Post-purchase account creation prompt
 * 
 * Features:
 * - Pre-filled customer info from order
 * - Password-only registration (email already captured)
 * - One-time token validation
 * - Auto-login after account creation
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AccountCreationModal.css';

const AccountCreationModal = ({ 
  isVisible, 
  onClose, 
  customerEmail, 
  customerName, 
  accountCreationToken 
}) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isVisible) return null;

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/create-from-order`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: accountCreationToken,
            password,
            agreeToTerms
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account');
      }

      // Store JWT token
      localStorage.setItem('ogestyle_token', data.token);

      // Success - redirect to customer dashboard
      navigate('/customer/dashboard');

    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-modal-overlay" onClick={onClose}>
      <div className="account-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h2>🎉 Create Your Account</h2>
          <p>Complete your account to track orders, rebook favorites, and get exclusive offers!</p>
        </div>

        <div className="benefits-grid">
          <div className="benefit-item">
            <span className="benefit-icon">📦</span>
            <span className="benefit-text">Track all orders</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">⭐</span>
            <span className="benefit-text">Easy rebooking</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">🎁</span>
            <span className="benefit-text">Exclusive offers</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">💝</span>
            <span className="benefit-text">Save favorites</span>
          </div>
        </div>

        <form onSubmit={handleCreateAccount} className="account-form">
          <div className="prefilled-info">
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{customerName.firstName} {customerName.lastName}</span>
            </div>
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{customerEmail}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Create Password *</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
            />
            <small>At least 8 characters, 1 uppercase, 1 lowercase, 1 number</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
            />
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="agreeToTerms"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
            />
            <label htmlFor="agreeToTerms">
              I agree to the <a href="/terms" target="_blank">Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank">Privacy Policy</a>
            </label>
          </div>

          {error && (
            <div className="error-message">
              <span>⚠️</span>
              {error}
            </div>
          )}

          <button type="submit" className="create-btn" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Free Account'}
          </button>

          <div className="divider">
            <span>OR</span>
          </div>

          <div className="oauth-options">
            <button 
              type="button" 
              className="oauth-btn google-btn"
              onClick={() => {
                const token = accountCreationToken;
                window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/google?token=${token}`;
              }}
            >
              <svg className="oauth-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
            <button 
              type="button" 
              className="oauth-btn facebook-btn"
              onClick={() => {
                const token = accountCreationToken;
                window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/facebook?token=${token}`;
              }}
            >
              <svg className="oauth-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </button>
            <button 
              type="button" 
              className="oauth-btn apple-btn"
              onClick={() => {
                const token = accountCreationToken;
                window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/apple?token=${token}`;
              }}
            >
              <svg className="oauth-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#000000" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Apple
            </button>
          </div>

          <button type="button" className="later-btn" onClick={onClose}>
            Maybe Later
          </button>
        </form>

        <p className="security-note">
          🔒 Your information is secure and will never be shared with third parties.
        </p>
      </div>
    </div>
  );
};

export default AccountCreationModal;
